import type { PaymentKitErrors } from "../types";
import { definePaymentMethod, getOrCreateCheckoutRequestId, toFraudMetadataWire } from "../utils";
import { type BeforeConfirmOptions, runBeforeConfirm } from "./before-confirm";
import {
  StripeLinkAdapter,
  type StripeLinkBillingDetails,
  type StripeLinkConfirmResult,
  StripeLinkMockScenario,
} from "./stripe-link-adapter";

// =============================================================================
// Pure function port (mirrors app/domain/payments/stripe_link/express.py).
// The express button shows iff Link is advertised AND the shopper has an
// authenticated Link session; otherwise Link is offered as a selectable tile.
// `ExpressXorTile` holds by construction: tile = advertised && !express.
// =============================================================================
export function shouldShowLinkExpressButton(linkSessionAuthenticated: boolean, linkAdvertised: boolean): boolean {
  return linkAdvertised && linkSessionAuthenticated;
}

// =============================================================================
// Types
// =============================================================================
export type StripeLinkCustomerInfo = {
  first_name: string;
  last_name: string;
  email?: string;
};

/**
 * Split the single `name` Link returns into first/last, matching how the other
 * wallet paths thread `payer_first_name` / `payer_last_name`. Everything after
 * the first token is the last name, so "Mateus Galdino Silveira" keeps both
 * surnames rather than dropping one.
 */
export function splitPayerName(name: string | undefined): {
  firstName?: string;
  lastName?: string;
} {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) return {};
  if (parts.length === 1) return { firstName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

export type StripeLinkStartResponse = {
  stripe_pk: string;
  amount: number;
  currency: string;
  mode: "payment" | "setup";
  /** True when the saved Link PM must carry the off-session mandate (setup
   * mode or recurring line items). Optional so an older backend that doesn't
   * send it degrades to the previous payment-mode behavior (no mandate). */
  save_for_future_use?: boolean;
};

export type StripeLinkConfirmResponse = {
  charge_status: "success" | "fail" | "pending";
  transaction_id?: string;
  error_code?: string;
  error_message_for_customer?: string;
  error_message_for_debug?: string;
  checkout_attempt_id?: string;
  checkout_session_id?: string;
  payment_intent_id?: string;
  customer_id?: string;
  payment_method_id?: string;
  processor_used?: string;
  subscription_id?: string;
  invoice_id?: string;
  invoice_number?: number;
  card_brand?: string;
  card_last4?: string;
};

export type StripeLinkSubmitOptions = BeforeConfirmOptions & {
  /** Stripe processor external id (required — Link is Stripe-only). */
  processorId: string;
  customerInfo: StripeLinkCustomerInfo;
  mockScenario?: StripeLinkMockScenario;
};

// Exported so consumers (and the inferred PaymentKit instance type via
// `onLinkResult`) can name it — an unexported type in a public externalFunc
// signature triggers TS2742 in the customer-portal build.
export type StripeLinkResult =
  | { data: { [key: string]: unknown }; errors?: never }
  | { data?: undefined; errors: PaymentKitErrors };

// =============================================================================
// API helper (same shape as apple-pay.ts / google-pay.ts)
// =============================================================================
async function apiCall<T>(
  url: string,
  options: RequestInit,
  checkoutRequestId?: string,
): Promise<{ data?: T; error?: string }> {
  const headers = new Headers(options.headers);
  if (checkoutRequestId) {
    headers.set("x-request-id", checkoutRequestId);
  }
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    return { error: errorMessage };
  }
  return { data: await response.json() };
}

function getMockScenarioStr(scenario?: StripeLinkMockScenario): string | undefined {
  return scenario && scenario !== StripeLinkMockScenario.None ? scenario : undefined;
}

async function callStartEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  processorId: string,
  checkoutRequestId?: string,
): Promise<{ data?: StripeLinkStartResponse; error?: string }> {
  // Side-effect-free read: returns stripe_pk + amount/currency/mode so we can
  // mount the isolated Link element. No customer/attempt/PI is created here.
  return apiCall<StripeLinkStartResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/stripe-link/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ processor_id: processorId }),
    },
    checkoutRequestId,
  );
}

async function callConfirmEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  processorId: string,
  paymentMethodId: string,
  options: StripeLinkSubmitOptions,
  billingDetails: StripeLinkBillingDetails | undefined,
  mockScenarioStr: string | undefined,
  checkoutRequestId?: string,
  // What /start armed the Link element with — echoed so the backend can detect
  // drift (PAY-1879): the total is net of customer credit, and credit spent
  // between arming and confirm would otherwise land the charge ABOVE it.
  expectedSheet?: { amount: number; currency: string },
): Promise<StripeLinkResult> {
  const payerName = splitPayerName(billingDetails?.name);
  const result = await apiCall<StripeLinkConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/stripe-link/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: processorId,
        customer_info: options.customerInfo,
        fraud_metadata: toFraudMetadataWire(checkoutRequestId),
        payment_method_id: paymentMethodId,
        // PAY-1553: the whole payer identity Link gave us, not just the email.
        // The backend seeds the customer with these before it is created, so a
        // Link shopper is never anonymous and repeat purchases dedupe on email.
        payer_email: billingDetails?.email,
        payer_first_name: payerName.firstName,
        payer_last_name: payerName.lastName,
        payer_phone: billingDetails?.phone,
        payer_billing_address: billingDetails?.address,
        expected_amount: expectedSheet?.amount,
        expected_currency: expectedSheet?.currency,
        mock_scenario: mockScenarioStr,
      }),
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return { errors: { stripe_link: result.error || "Failed to confirm Stripe Link payment" } };
  }

  const data = result.data;
  if (data.charge_status === "success") {
    return {
      data: {
        id: data.transaction_id,
        checkoutAttemptId: data.checkout_attempt_id,
        checkoutSessionId: data.checkout_session_id ?? secureToken,
        state: "checkout_succeeded",
        paymentIntentId: data.payment_intent_id,
        customerId: data.customer_id,
        paymentMethodId: data.payment_method_id,
        processorUsed: data.processor_used,
        subscriptionId: data.subscription_id,
        invoiceId: data.invoice_id,
        invoiceNumber: data.invoice_number,
        cardBrand: data.card_brand,
        cardLast4: data.card_last4,
        errorCode: data.error_code,
        errorMessageForCustomer: data.error_message_for_customer,
        errorMessageForDebug: data.error_message_for_debug,
      },
    };
  }
  return {
    errors: {
      stripe_link: data.error_message_for_customer || data.error_message_for_debug || "Stripe Link payment failed",
    },
  };
}

// =============================================================================
// Payment Method Definition
// =============================================================================
//
// Unlike Apple/Google Pay (a merchant-rendered button that calls
// `paymentKit.submit()`), Stripe Link IS the button: Stripe renders the
// `expressCheckout` element and fires its own `confirm`. So the integration is
// callback-driven — the merchant mounts the button into a Link-owned container
// and observes results via `onLinkResult`. `submitPayment` is therefore not the
// entry point for Link and returns a directive error if called directly.
const StripeLinkPaymentMethod = definePaymentMethod((paymentKitStates) => {
  const { apiBaseUrl, secureToken, environment } = paymentKitStates;

  const adapter = new StripeLinkAdapter();
  let linkAdvertised = false;
  let sessionAuthenticated = false;
  let processorId: string | null = null;
  // The total /start armed the Link element with, echoed on confirm (PAY-1879).
  let armedSheet: { amount: number; currency: string } | null = null;
  // A refreshed /start that waits for the open Link window to close (see rearm()).
  let pendingStart: { generation: number; start: StripeLinkStartResponse } | null = null;
  let rearmSeq = 0;
  // Bumped by every resetLink() and every initStripeLink(). autoInit() captures
  // the value at entry and bails after each await if it changed — so a teardown
  // or re-init that lands mid-init (React strict-mode double-invoke, or a
  // dependency change) aborts the stale run instead of mounting a second
  // (orphaned) Link element into document.body.
  let generation = 0;

  let onReadyCallbacks: ((ready: boolean) => void)[] = [];
  let onAuthChangeCallbacks: ((showExpress: boolean) => void)[] = [];
  let onResultCallbacks: ((result: StripeLinkResult) => void)[] = [];
  let submitOptions: StripeLinkSubmitOptions | null = null;

  function emitAuthState(): void {
    const showExpress = shouldShowLinkExpressButton(sessionAuthenticated, linkAdvertised);
    for (const cb of onAuthChangeCallbacks) cb(showExpress);
  }

  // Full reset: unmount the Link element and drop all observers/callbacks/state.
  // Shared by the React effect teardown and the PaymentKit-wide cleanup so a
  // re-init (strict-mode double-invoke or dependency change) never stacks a
  // second button or accumulates duplicate callbacks.
  function resetLink(): void {
    // Invalidate any in-flight autoInit() so it aborts at its next await.
    generation += 1;
    adapter.unmount();
    adapter.setInstanceLive(false);
    onReadyCallbacks = [];
    onAuthChangeCallbacks = [];
    onResultCallbacks = [];
    submitOptions = null;
    processorId = null;
    armedSheet = null;
    pendingStart = null;
    linkAdvertised = false;
    sessionAuthenticated = false;
  }

  // Wire the adapter's confirm event to the /stripe-link/confirm POST.
  adapter.onConfirm(async (confirm: StripeLinkConfirmResult) => {
    if (!confirm.success) {
      const errorMsg = "cancelled" in confirm ? "Stripe Link cancelled by user" : confirm.error || "Stripe Link failed";
      for (const cb of onResultCallbacks) cb({ errors: { stripe_link: errorMsg } });
      return;
    }
    if (!processorId || !submitOptions) {
      for (const cb of onResultCallbacks)
        cb({ errors: { stripe_link: "Stripe Link not initialized — call setSubmitOptions first" } });
      return;
    }
    // Link charges outside submit(), so it ends a pending BNPL submit itself.
    paymentKitStates._onPaymentStarting?.("stripe_link");
    // Snapshot before any await: resetLink() nulls both, so a teardown landing
    // while the merchant hook runs would otherwise hand `null` to the confirm
    // call. The generation snapshot lets us bail on that teardown instead.
    const activeProcessorId = processorId;
    const activeOptions = submitOptions;
    // The total the Link window showed (the adapter records it per window).
    const activeSheet = confirm.shownSheet ?? armedSheet;
    const myGeneration = generation;
    // resetLink() also drops the result callbacks, so keep the ones this confirm
    // started with: a teardown mid-validation still tells the merchant.
    const activeResultCallbacks = onResultCallbacks;

    // Merchant pre-charge validation (PAY-1540). Link's own flow already keeps
    // the tap gesture-safe (Stripe owns the button and we resolve its click
    // synchronously), so the hook exists purely to gate the charge: the shopper
    // has authorized in the Link window and nothing is charged until the POST
    // below.
    const gate = await runBeforeConfirm(activeOptions.beforeConfirm, activeOptions.beforeConfirmTimeoutMs);
    if (myGeneration !== generation) {
      // Torn down or re-initialized while beforeConfirm ran: nothing is charged.
      for (const cb of activeResultCallbacks)
        cb({ errors: { stripe_link: "Stripe Link was re-initialized before the payment was confirmed" } });
      return;
    }
    if (!gate.proceed) {
      for (const cb of onResultCallbacks) cb({ errors: { stripe_link: gate.error } });
      return;
    }

    const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
    const result = await callConfirmEndpoint(
      apiBaseUrl,
      secureToken,
      activeProcessorId,
      confirm.paymentMethodId,
      activeOptions,
      confirm.billingDetails,
      getMockScenarioStr(activeOptions.mockScenario),
      checkoutRequestId,
      activeSheet ?? undefined,
    );
    // PAY-1886: re-arm after a failed confirm, so the next attempt echoes the
    // current total instead of the figure the drift guard just refused (until
    // now every retry failed until a page reload). rearm() updates the element
    // in place: an unmount + autoInit() here would remove the button, since
    // mounting is the merchant's call.
    if (result.errors && myGeneration === generation) {
      void rearm().then((r) => {
        if (!r.success) console.warn("[StripeLink] re-arm after a failed confirm failed:", r.error);
      });
    }
    for (const cb of onResultCallbacks) cb(result);
  });

  // Re-read the total from /start and update the mounted Link element in place.
  // The total is net of the session customer's spendable credit (PAY-1879), so
  // it changes when the merchant attaches a customer to the session (PATCH
  // customer_id). Nothing is unmounted, so this is safe while the Link window is
  // open — unlike a re-init, which tears the button down.
  //
  // An open Link window keeps showing the total it opened with, and Stripe
  // charges what we echo. So while the window is open the new total waits for
  // it to close: a confirm meanwhile echoes the total the shopper saw, the drift
  // guard refuses it, and the retry opens a window with the new total. The
  // shopper is never charged a figure they were not shown.
  function applyStart(start: StripeLinkStartResponse): { success: boolean; error?: string } {
    const update = adapter.updateAmount({ mode: start.mode, amount: start.amount, currency: start.currency });
    if (!update.success) return update;
    armedSheet = { amount: start.amount, currency: start.currency };
    return { success: true };
  }

  adapter.onWindowClosed(() => {
    const pending = pendingStart;
    pendingStart = null;
    if (pending && pending.generation === generation) applyStart(pending.start);
  });

  async function rearm(): Promise<{ success: boolean; deferred?: boolean; error?: string }> {
    if (!processorId || !linkAdvertised) return { success: false, error: "Stripe Link is not initialized" };
    const myGeneration = generation;
    // Two overlapping re-arms (a merchant refresh and the one after a failed
    // confirm) share a generation: only the newest may apply its total.
    rearmSeq += 1;
    const mySeq = rearmSeq;
    const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
    const startResult = await callStartEndpoint(apiBaseUrl, secureToken, processorId, checkoutRequestId);
    if (myGeneration !== generation) return { success: false, error: "Stripe Link was re-initialized" };
    if (mySeq !== rearmSeq) return { success: false, error: "Superseded by a newer refresh" };
    if (startResult.error || !startResult.data) {
      return { success: false, error: startResult.error || "Failed to refresh Stripe Link" };
    }
    if (adapter.isWindowOpen()) {
      pendingStart = { generation: myGeneration, start: startResult.data };
      return { success: true, deferred: true };
    }
    pendingStart = null;
    return applyStart(startResult.data);
  }

  // Auto-initialize: fetch /start, set up the isolated Link element, and begin
  // silent authenticated-session detection. Link only initializes here when the
  // backend says it's fulfillable, so on a non-Stripe checkout Stripe.js for
  // Link never loads (cross-processor isolation, design §4.6).
  async function autoInit(): Promise<void> {
    // Snapshot the generation at entry; if resetLink() bumps it while we await,
    // this run is stale and must not touch the adapter or emit state.
    const myGeneration = generation;
    const isStale = (): boolean => myGeneration !== generation;
    try {
      // The merchant must hand us the Stripe processor id before init can run;
      // until then we stay un-advertised (fail-closed).
      if (!processorId) return;
      const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
      const startResult = await callStartEndpoint(apiBaseUrl, secureToken, processorId, checkoutRequestId);
      if (isStale()) return;
      if (startResult.error || !startResult.data) {
        // 400 here means Link is not available for this checkout — stay quiet.
        linkAdvertised = false;
        emitAuthState();
        return;
      }
      const start = startResult.data;
      armedSheet = { amount: start.amount, currency: start.currency };
      if (!adapter.initialize(start.stripe_pk)) {
        linkAdvertised = false;
        return;
      }
      const setup = adapter.setupElements({
        mode: start.mode,
        amount: start.amount,
        currency: start.currency,
        saveForFutureUse: start.save_for_future_use,
      });
      if (isStale()) {
        // A teardown raced us — undo the element setup we just did so no hidden
        // Link element is orphaned in the DOM.
        adapter.unmount();
        return;
      }
      if (!setup.success) {
        linkAdvertised = false;
        return;
      }
      linkAdvertised = true;
      for (const cb of onReadyCallbacks) cb(true);
      adapter.detectAuthenticatedSession((authenticated) => {
        if (isStale()) return;
        sessionAuthenticated = authenticated;
        emitAuthState();
      });
      emitAuthState();
    } catch (err) {
      console.error("[StripeLink] auto-init failed:", err);
      linkAdvertised = false;
    }
  }

  return {
    name: "stripe_link",
    externalFuncs: {
      /**
       * Provide the Stripe processor id + customer info, then initialize Link.
       * Must be called before mounting (Link is Stripe-only and non-fungible).
       */
      initStripeLink: (options: StripeLinkSubmitOptions): Promise<void> => {
        // Self-cleaning re-init: invalidate any in-flight autoInit() and tear
        // down a prior run's elements so two overlapping inits can't both set
        // up elements (orphaning the earlier hidden auth element). Registered
        // callbacks are kept — consumers register them before calling init.
        generation += 1;
        adapter.unmount();
        adapter.setInstanceLive(true);
        submitOptions = options;
        processorId = options.processorId;
        return autoInit();
      },
      /** Mount the Link express button into `selector` (a Link-owned container). */
      mountLinkButton: (selector: string): { success: boolean; error?: string } => adapter.mountExpressButton(selector),
      /** Notified true when the express button is mounted and ready. */
      onLinkReady: (callback: (ready: boolean) => void): void => {
        onReadyCallbacks.push(callback);
      },
      /**
       * Notified with `showExpress` — true to render the express button, false
       * to fall back to the Link tile (ExpressXorTile). Reflects the pure
       * `should_show_link_express_button` over the live authenticated session.
       */
      onLinkAuthChange: (callback: (showExpress: boolean) => void): void => {
        onAuthChangeCallbacks.push(callback);
      },
      /** Notified with the final result after the shopper confirms with Link. */
      onLinkResult: (callback: (result: StripeLinkResult) => void): void => {
        onResultCallbacks.push(callback);
      },
      /**
       * Tear down the Link element + observers + callbacks. Call from a React
       * effect cleanup so re-mounts don't stack duplicate buttons. Idempotent.
       */
      teardownStripeLink: (): void => resetLink(),
      /**
       * Re-read the checkout total and update the Link button in place. Call it
       * after changing what the shopper pays without a new session — e.g. after
       * attaching a customer (PATCH customer_id), whose spendable credit lowers
       * the total. Unlike teardown + initStripeLink, nothing is torn down, so it
       * is safe while the Link window is open. In that case the new total applies
       * once the window closes (`deferred: true`): a payment from the open window
       * fails with the old total, and the retry shows the new one.
       */
      refreshStripeLink: (): Promise<{ success: boolean; deferred?: boolean; error?: string }> => rearm(),
    },
    internalFuncs: {
      submitPayment: async (_fields, _options) => {
        return {
          errors: {
            stripe_link:
              "Stripe Link is confirmed via its mounted express button, not paymentKit.submit(). " +
              "Use initStripeLink() + mountLinkButton() and observe onLinkResult().",
          },
        };
      },
      cleanup: () => resetLink(),
    },
  };
});

export { StripeLinkMockScenario };

export default StripeLinkPaymentMethod;
